import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Text,
  Td,
  ModalHeader,
  ModalOverlay,
  useColorModeValue,
  Center,
  Spinner
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import { FC, ReactNode, useEffect, useState } from 'react'
import { BiSliderAlt } from 'react-icons/bi'

import LayoutToolsButton from '@component/Layout/Tools/Actions/Components/Button'

export interface IHistoricoItem {
  usuarioEmail: string
  id: number
  empresaId?: number
  acao: string
  usuarioId: number
  dataAcao: string
}

export interface HistoricColumn {
  header: string
  render: (item: IHistoricoItem) => ReactNode
}

// Colunas padrão dos históricos da Config Compras
export const HISTORIC_COLUMNS = {
  data: {
    header: 'Data',
    render: (item: IHistoricoItem) =>
      item.dataAcao ? dayjs(item.dataAcao).format('DD/MM/YYYY HH:mm:ss') : '—'
  },
  usuario: {
    header: 'Usuário',
    render: (item: IHistoricoItem) => item.usuarioEmail
  },
  empresa: {
    header: 'Empresa',
    render: (item: IHistoricoItem) => item.empresaId
  },
  acao: {
    header: 'Ação',
    render: (item: IHistoricoItem) => item.acao
  }
}

interface HistoricModalProps {
  title: string
  // Busca os registros; recebe a empresa já validada
  fetcher: (empresaId: number) => Promise<IHistoricoItem[]>
  // Empresa selecionada no topo da tela (sem empresa o botão fica desabilitado)
  empresaId?: string | number | null
  columns: HistoricColumn[]
  emptyMessage: string
  // Muda quando outro filtro (ex.: comprador) exige nova busca
  reloadKey?: string | number | null
}

const HistoricModal: FC<HistoricModalProps> = ({
  title,
  fetcher,
  empresaId,
  columns,
  emptyMessage,
  reloadKey
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [historic, setHistoric] = useState<IHistoricoItem[]>([])
  const colorSpinner = useColorModeValue('black', 'white')

  const empresa = Number(empresaId)
  const hasEmpresa = !!empresaId && !isNaN(empresa)

  useEffect(() => {
    if (!isOpen || !hasEmpresa) return
    // Descarta a resposta se a empresa/filtro mudar ou o modal fechar antes dela chegar
    let cancelled = false
    setLoading(true)
    setError(null)
    fetcher(empresa)
      .then(items => {
        if (cancelled) return
        setHistoric(Array.isArray(items) ? items : [])
      })
      .catch((err: any) => {
        if (cancelled) return
        setHistoric([])
        // Erro de rede/servidor não é "sem histórico": mostra o erro
        setError(
          err?.response?.data?.message ||
            'Não foi possível carregar o histórico. Tente novamente.'
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // fetcher é recriado a cada render pelos wrappers; a busca depende só de empresa/filtro
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasEmpresa, empresa, reloadKey])

  return (
    <>
      <LayoutToolsButton
        isDisabledDefault={!hasEmpresa}
        label="Histórico"
        onClick={() => setIsOpen(true)}
      />

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="5xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            paddingTop={2}
            paddingBottom={2}
            paddingLeft={4}
            paddingRight={4}
            textColor={'gray.200'}
            background={'#1d5840'}
            borderTopLeftRadius={5}
            borderTopRightRadius={5}
            borderBottom="3px solid #ECC94B"
          >
            <Flex>
              <Box p={2}>
                <BiSliderAlt />
              </Box>
              <Box p={2} pt={1} w={1200}>
                {title}
              </Box>
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody bg="white">
            <Box overflowX="auto" maxHeight={490}>
              {loading ? (
                <Box h={40}>
                  <Center pt={10}>
                    <Flex
                      direction="column"
                      alignItems="center"
                      textAlign="center"
                    >
                      <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="gray.200"
                        color={colorSpinner}
                        size="xl"
                      />
                      <Text
                        color={colorSpinner}
                        marginTop="4"
                        fontSize="xl"
                        fontWeight="bold"
                      >
                        Buscando dados
                      </Text>
                    </Flex>
                  </Center>
                </Box>
              ) : error ? (
                <Alert status="error" borderRadius="md" my={4}>
                  <AlertIcon />
                  {error}
                </Alert>
              ) : (
                <>
                  <Table>
                    <Thead>
                      <Tr bg="gray.200">
                        {columns.map(column => (
                          <Th key={column.header} color="black">
                            {column.header}
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody overflowY="auto">
                      {historic.map((item, index) => (
                        <Tr key={`historic-${item.id ?? 'x'}-${index}`}>
                          {columns.map(column => (
                            <Td key={column.header}>{column.render(item)}</Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {historic.length === 0 && (
                    <Box textAlign="center" py={8}>
                      <Text color="gray.500">{emptyMessage}</Text>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

export default HistoricModal
